import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Chart, ChartModule } from 'angular-highcharts';
import { IconComponent } from '../../../../shared/icons/icon.component';

const FONT = "'Plus Jakarta Sans','Outfit',sans-serif";
const BASE: any = {
  credits: { enabled: false },
  title: { text: undefined },
  chart: { backgroundColor: 'transparent', style: { fontFamily: FONT } },
  tooltip: { backgroundColor: '#1e293b', borderColor: 'transparent', borderRadius: 8, shadow: false, style: { color: '#f8fafc', fontSize: '12px', fontFamily: FONT }, padding: 12 },
  xAxis: { lineColor: '#e2e8f0', tickColor: '#e2e8f0', gridLineColor: 'transparent', labels: { style: { fontSize: '11px', color: '#94a3b8', fontFamily: FONT } } },
  yAxis: { gridLineColor: '#f1f5f9', tickColor: 'transparent', lineColor: 'transparent', labels: { style: { fontSize: '11px', color: '#94a3b8', fontFamily: FONT } } },
  legend: { itemStyle: { fontWeight: '500', fontSize: '12px', color: '#475569', fontFamily: FONT }, itemHoverStyle: { color: '#0d9488' } },
  plotOptions: {
    column: { borderWidth: 0, borderRadius: 6 } as any,
    pie: { borderWidth: 2, borderColor: '#ffffff', innerSize: '50%' } as any,
    spline: { marker: { symbol: 'circle' } } as any,
  },
};

function merge<T extends object>(base: T, ...overrides: Partial<T>[]): T {
  return overrides.reduce<T>((acc, o) => {
    Object.keys(o).forEach(k => {
      const key = k as keyof T;
      const bv = acc[key], ov = o[key];
      (acc as any)[key] = (bv && ov && typeof bv === 'object' && typeof ov === 'object' && !Array.isArray(bv))
        ? merge(bv as object, ov as object) : ov;
    });
    return acc;
  }, { ...base } as T);
}
import { TelecallersApiService, TelecallerAnalyticsResponse, UpcomingFollowup, TelecallerPerformanceResponse } from '../../../../core/services/telecallers/telecallers-api.service';
import { AuthService } from '../../../../core/services/auth/auth.service';
import { SkeletonLoaderComponent } from '../../../../shared/components/skeleton-loader/skeleton-loader.component';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { LookupDataService } from '../../../../core/services/lookup-data.service';
import { PatientsApiService } from '../../../../core/services/patients/patients-api.service';
import { StatsCardComponent } from '../../../../shared/components/stats-card/stats-card';
import { CallModalComponent } from '../../../../shared/components/call-modal/call-modal.component';
import { EmptyStateComponent } from '../../../../shared/components/empty-state/empty-state.component';
import { DrawerComponent } from '../../../../shared/components/drawer/drawer.component';

@Component({
  selector: 'app-telecaller-dashboard',
  standalone: true,
  imports: [CommonModule, ChartModule, IconComponent, SkeletonLoaderComponent, FormsModule, NgSelectModule, StatsCardComponent, CallModalComponent, EmptyStateComponent, DrawerComponent],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
})
export class TelecallerDashboardComponent implements OnInit, OnDestroy {
  analytics: TelecallerAnalyticsResponse | null = null;
  isLoading = false;
  isInitialLoad = true;
  isLoadingFollowups = true;
  isLoadingPerformance = true;
  error = '';
  
  followUpChart: Chart = new Chart({ series: [] });
  patientStatusChart: Chart = new Chart({ series: [] });
  totalCallsChart: Chart = new Chart({ series: [] });
  totalSalesChart: Chart = new Chart({ series: [] });
  upcomingFollowups: UpcomingFollowup[] = [];
  performance: TelecallerPerformanceResponse | null = null;
  selectedPeriod: string = 'all';

  // Modal States
  showCallModal: boolean = false;
  selectedPatient: any = null; // Can be a Patient object or an UpcomingFollowup object

  // Follow-up Modal States
  showFollowupModal: boolean = false;
  selectedPatientForFollowup: any = null;
  patientOptions: any[] = [];
  isLoadingPatients = false;
  followupData = {
    patient_id: '',
    date: '',
    time: '',
    note: ''
  };
  followupError = '';
  isSaving = false;

  // Drawer of All Follow-ups
  showAllFollowupsDrawer: boolean = false;
  drawerFollowups: UpcomingFollowup[] = [];
  isLoadingDrawerFollowups: boolean = false;
  drawerFilter: string = 'today';
  drawerSearchQuery: string = '';

  // Filter options
  followupFilterOptions = [
    { id: 'today', name: 'Today' },
    { id: 'this_week', name: 'This Week' },
    { id: 'this_month', name: 'This Month' },
    { id: 'all', name: 'All' },
    { id: 'custom', name: 'Custom' }
  ];
  selectedFollowupFilter = 'today';
  get isFollowupRangePending(): boolean {
    return this.selectedFollowupFilter === 'custom' && (!this.customStartDate || !this.customEndDate);
  }
  get isDrawerRangePending(): boolean {
    return this.drawerFilter === 'custom' && (!this.drawerCustomStartDate || !this.drawerCustomEndDate);
  }
  customStartDate = '';
  customEndDate = '';
  drawerCustomStartDate = '';
  drawerCustomEndDate = '';

  performanceFilterOptions = [
    { id: 'last_7_days', name: 'This Week' },
    { id: 'last_30_days', name: 'This Month' },
    { id: 'all', name: 'All Time' }
  ];

  constructor(
    private telecallerService: TelecallersApiService,
    private authService: AuthService,
    private router: Router,
    private lookupDataService: LookupDataService,
    private patientsService: PatientsApiService
  ) {}

  ngOnInit() {
    this.fetchAnalytics();
    this.fetchUpcomingFollowups();
    this.fetchPerformance('all');
  }

  ngOnDestroy(): void {}

  private checkLoadingState() {
    if (!this.isLoading && !this.isLoadingFollowups && !this.isLoadingPerformance) {
      this.isInitialLoad = false;
    }
  }

  fetchAnalytics() {
    const user = this.authService.getCurrentUser();
    if (!user || (!user.id && !user.user_id)) {
      this.error = 'User not found';
      return;
    }

    const telecallerId = user.id;

    this.isLoading = true;
    this.telecallerService.getTelecallerAnalytics(telecallerId).subscribe({
      next: (data) => {
        this.analytics = data;
        this.processAnalyticsData(data);
        this.isLoading = false;
        this.checkLoadingState();
      },
      error: (err) => {
        console.error('Error fetching analytics:', err);
        this.error = 'Failed to load dashboard data';
        this.isLoading = false;
        this.checkLoadingState();
      }
    });
  }

  processAnalyticsData(data: TelecallerAnalyticsResponse) {
    if (data.followup_trends && data.followup_trends.data_points) {
      const pts = data.followup_trends.data_points.slice(-15);
      this.followUpChart = new Chart(merge(BASE, {
        colors: ['#0d9488'],
        xAxis: { categories: pts.map(dp => dp.date) },
        series: [{ type: 'spline', name: data.followup_trends.title, data: pts.map(dp => dp.value) }],
      }));
    }

    if (data.patient_status_distribution) {
      this.patientStatusChart = new Chart(merge(BASE, {
        colors: ['#0d9488', '#3b82f6', '#f59e0b'],
        series: [{
          type: 'pie',
          name: 'Status',
          innerSize: '50%',
          data: [
            { name: 'Completed', y: data.patient_status_distribution.completed },
            { name: 'Active', y: data.patient_status_distribution.active },
            { name: 'Overdue', y: data.patient_status_distribution.overdue },
          ],
        }],
      }));
    }

    if (data.calls_graph && data.calls_graph.data_points) {
      const pts = data.calls_graph.data_points.slice(-15);
      this.totalCallsChart = new Chart(merge(BASE, {
        colors: ['#0d9488'],
        xAxis: { categories: pts.map(dp => dp.date || dp.day || '') },
        series: [{ type: 'column', name: data.calls_graph.title, data: pts.map(dp => dp.value) }],
      }));
    }

    if (data.sales_graph && data.sales_graph.data_points) {
      const pts = data.sales_graph.data_points.slice(-15);
      this.totalSalesChart = new Chart(merge(BASE, {
        colors: ['#6366f1'],
        xAxis: { categories: pts.map(dp => dp.date || dp.day || '') },
        series: [{ type: 'column', name: data.sales_graph.title, data: pts.map(dp => dp.value) }],
      }));
    }
  }

  fetchUpcomingFollowups(filterType: string = this.selectedFollowupFilter) {
    // Custom needs a complete range; wait until both dates are picked.
    if (filterType === 'custom' && (!this.customStartDate || !this.customEndDate)) {
      this.upcomingFollowups = [];
      this.isLoadingFollowups = false;
      return;
    }
    this.isLoadingFollowups = true;
    this.upcomingFollowups = [];
    this.telecallerService.getUpcomingFollowups(filterType, 50, this.customStartDate, this.customEndDate).subscribe({
      next: (data) => {
        this.upcomingFollowups = data.followups || [];
        this.isLoadingFollowups = false;
        this.checkLoadingState();
      },
      error: (err: any) => {
        console.error('Error fetching upcoming followups:', err);
        this.upcomingFollowups = [];
        this.isLoadingFollowups = false;
        this.checkLoadingState();
      }
    });
  }

  fetchPerformance(period: string = 'last_7_days') {
    this.selectedPeriod = period;
    this.isLoadingPerformance = true;
    this.telecallerService.getTelecallerPerformance(period).subscribe({
      next: (data) => {
        this.performance = data;
        this.isLoadingPerformance = false;
        this.checkLoadingState();
      },
      error: (err: any) => {
        console.error('Error fetching performance:', err);
        this.isLoadingPerformance = false;
        this.checkLoadingState();
      }
    });
  }

  onPeriodChange(event: any) {
    const period = event.target.value;
    this.fetchPerformance(period);
  }

  // --- Calling Methods (handled by CallModalComponent) ---

  openCallModal(patient: any): void {
    this.selectedPatient = patient;
    this.showCallModal = true;
  }

  closeCallModal(): void {
    this.showCallModal = false;
    this.selectedPatient = null;
  }

  // --- Quick Actions ---
  navigateToAddPatient(): void {
    this.router.navigate(['/patient-list/add-patient']);
  }

  openAddFollowupModal(): void {
    this.showFollowupModal = true;
    this.followupError = '';
    this.followupData = {
      patient_id: '',
      date: '',
      time: '',
      note: ''
    };
    this.loadPatients();
  }

  closeFollowupModal(): void {
    this.showFollowupModal = false;
    this.selectedPatientForFollowup = null;
    this.followupError = '';
  }

  loadPatients(): void {
    if (this.patientOptions.length > 0 || this.isLoadingPatients) return;

    this.isLoadingPatients = true;
    this.patientsService.getPatients(1, 1000).subscribe({
      next: (response: any) => {
        const data = response.data || response;
        const patients = data.patients || data || [];
        this.patientOptions = patients.map((p: any) => ({
          id: p._id || p.id,
          name: p.name || 'Unknown',
          contact_number: p.contact_number || ''
        }));
        this.isLoadingPatients = false;
      },
      error: (err: any) => {
        console.error('Error loading patients:', err);
        this.isLoadingPatients = false;
      }
    });
  }

  saveFollowup(): void {
    this.followupError = '';

    if (!this.followupData.patient_id || !this.followupData.date || !this.followupData.time) {
      this.followupError = 'Please fill in all required fields';
      return;
    }

    this.isSaving = true;
    const payload = {
      next_followup_date: `${this.followupData.date}T${this.followupData.time}:00`
    };

    this.patientsService.updatePatient(this.followupData.patient_id, payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.closeFollowupModal();
        this.fetchUpcomingFollowups(); // Refresh the dashboard list
        if (this.showAllFollowupsDrawer) {
          this.fetchDrawerFollowups(); // Refresh drawer list if open
        }
      },
      error: (err: any) => {
        console.error('Error saving followup:', err);
        this.followupError = err.error?.message || 'Failed to save followup. Please try again.';
        this.isSaving = false;
      }
    });
  }

  // --- All Follow-ups Drawer Methods ---

  openAllFollowupsDrawer(): void {
    this.showAllFollowupsDrawer = true;
    this.drawerSearchQuery = '';
    this.drawerFilter = this.selectedFollowupFilter || 'today';
    this.drawerCustomStartDate = this.customStartDate;
    this.drawerCustomEndDate = this.customEndDate;
    this.fetchDrawerFollowups(this.drawerFilter);
  }

  closeAllFollowupsDrawer(): void {
    this.showAllFollowupsDrawer = false;
  }

  fetchDrawerFollowups(filterType: string = this.drawerFilter): void {
    this.drawerFilter = filterType;
    if (filterType === 'custom' && (!this.drawerCustomStartDate || !this.drawerCustomEndDate)) {
      this.drawerFollowups = [];
      this.isLoadingDrawerFollowups = false;
      return;
    }
    this.isLoadingDrawerFollowups = true;
    this.drawerFollowups = [];
    this.telecallerService.getUpcomingFollowups(filterType, 100, this.drawerCustomStartDate, this.drawerCustomEndDate).subscribe({
      next: (data) => {
        this.drawerFollowups = data.followups || [];
        this.isLoadingDrawerFollowups = false;
      },
      error: (err: any) => {
        console.error('Error fetching drawer followups:', err);
        this.drawerFollowups = [];
        this.isLoadingDrawerFollowups = false;
      }
    });
  }

  get filteredDrawerFollowups(): UpcomingFollowup[] {
    let list = this.drawerFollowups;
    const query = (this.drawerSearchQuery || '').trim().toLowerCase();
    if (!query) {
      return list;
    }
    return list.filter(item => {
      const nameMatch = (item.name || '').toLowerCase().includes(query);
      const programMatch = (item.program_name || '').toLowerCase().includes(query);
      const phoneMatch = (item.contact_number || '').toLowerCase().includes(query);
      const pidMatch = (item.patient_pid || '').toLowerCase().includes(query);
      return nameMatch || programMatch || phoneMatch || pidMatch;
    });
  }

  getFollowupTimingStatus(dateStr: string): 'overdue' | 'today' | 'upcoming' {
    if (!dateStr) return 'upcoming';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'upcoming';
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    if (date < startOfToday) {
      return 'overdue';
    } else if (date <= endOfToday) {
      return 'today';
    } else {
      return 'upcoming';
    }
  }

  rescheduleFollowup(followup: UpcomingFollowup): void {
    this.showFollowupModal = true;
    this.followupError = '';
    const dateObj = new Date(followup.next_followup_date);
    const dateStr = !isNaN(dateObj.getTime()) ? dateObj.toISOString().split('T')[0] : '';
    const hours = !isNaN(dateObj.getTime()) ? String(dateObj.getHours()).padStart(2, '0') : '';
    const mins = !isNaN(dateObj.getTime()) ? String(dateObj.getMinutes()).padStart(2, '0') : '';
    const timeStr = hours && mins ? `${hours}:${mins}` : '';

    this.followupData = {
      patient_id: followup.patient_id || followup.id,
      date: dateStr,
      time: timeStr,
      note: ''
    };
    this.loadPatients();
  }
}
